import gsap from 'gsap'
import {Color, Group, Mesh, PlaneGeometry, ShaderMaterial, Vector4} from 'three'

import type {DitherParams, DitherRegistration} from '../../components/WebGLContext/WebGLContext.types'
import {bgColor, windowSize} from '../_config'
import {prefersReducedMotion} from '../_motionUtils'
import {DITHERING_DEFAULT_DOT_OPACITY} from '../_rawConfig'
import shaderNoiseFrag from '../_shaders/dithering/noise.frag'
import shaderRippleFrag from '../_shaders/dithering/ripple.frag'
import shaderWaveFrag from '../_shaders/dithering/wave.frag'
import vertexShader from '../_shaders/vertexShader'
import BayerTexture from './BayerTexture'
import DitheringAnimation from './DitheringAnimation'
import {BACKGROUND_FRAGMENT_SHADERS, VARIANTS} from './DitheringEffect.data'
import type {
  DitheringBackgroundType,
  DitheringCommonUniforms,
  DitheringUniforms,
  DitheringVariantUniforms,
  DitheringVariantUniformsNoiseGradient,
} from './DitheringEffect.types'

const IS_DEBUG = process.env.NODE_ENV !== 'production' && false

export default class DitheringEffect {
  bayerTexture: BayerTexture = new BayerTexture()
  group: Group = new Group()
  planes: Mesh[] = []
  commonUniforms: DitheringCommonUniforms = {
    uBgColor: {value: bgColor},
  }

  get isReducedMotion() {
    return prefersReducedMotion()
  }

  constructor() {
    this.bayerTexture.renderOnce()
  }

  updateRegisterDither(dithers: DitherRegistration[]) {
    // Check existing planes and remove those not in dithers
    // If new dither registrations are found, add planes for them
    // This method can be expanded based on how dithers are linked to planes
    const existingIds = this.planes.map(plane => plane.userData.ditherId)
    const newIds = dithers.map(dither => dither.id)

    // Remove planes not in newIds
    this.planes = this.planes.filter(plane => {
      if (!newIds.includes(plane.userData.ditherId)) {
        this.removePlane(plane)
        return false
      }
      return true
    })

    // Add new planes for newIds
    dithers.forEach(dither => {
      if (!existingIds.includes(dither.id)) {
        this.createPlane(dither.element, dither.id, dither.params)
      }
    })
  }

  resize() {
    this.planes.forEach(plane => {
      const ditheringAnimation: DitheringAnimation = plane.userData.ditheringAnimation
      const rect = plane.userData.domElement.getBoundingClientRect()

      if (plane.userData.backgroundType === 'noiseGradient') {
        ;(plane.userData.uniforms as DitheringUniforms).uAspect = {value: rect.width / rect.height}
      }

      ditheringAnimation.resize(rect.width, rect.height)
    })
  }

  update(frameDeltaScroll: number, frameDelta: number) {
    for (let i = 0; i < this.planes.length; i++) {
      const plane = this.planes[i]!
      const domElement = plane.userData.domElement
      const rect = domElement.getBoundingClientRect()
      const width = rect.width
      const height = rect.height
      const left = rect.left - windowSize.x / 2 + width / 2
      const top = (rect.top - windowSize.y / 2 + height / 2) * -1

      plane.position.set(left, top, 0)
      plane.scale.set(width, height, 1)

      // Detect if the plane is in the viewport
      const isInViewport = rect.bottom >= 0 && rect.right >= 0 && rect.top <= windowSize.y && rect.left <= windowSize.x
      const visibleWidth = Math.max(0, Math.min(rect.right, windowSize.x) - Math.max(rect.left, 0))
      const visibleHeight = Math.max(0, Math.min(rect.bottom, windowSize.y) - Math.max(rect.top, 0))
      const visibilityRatio = (visibleWidth * visibleHeight) / (width * height)

      if (isInViewport) {
        const ditheringAnimation: DitheringAnimation = plane.userData.ditheringAnimation
        const progress = plane.userData.progress

        if ((plane.userData.backgroundType as DitheringBackgroundType) === 'noiseGradient') {
          plane.userData.uniforms.uAspect.value = width / height
        }

        if (!plane.userData.hasEnteredViewport) {
          plane.userData.hasEnteredViewport = true

          if (plane.userData.fadeIn && !this.isReducedMotion) {
            this.animateBackgroundIn(plane)?.pause(0)
          }
        }

        if (!plane.userData.startTime) {
          if (
            !plane.userData.backgroundVisibilityThreshold ||
            visibilityRatio >= plane.userData.backgroundVisibilityThreshold
          ) {
            plane.userData.startTime = performance.now()

            if (plane.userData.fadeIn && !this.isReducedMotion) {
              this.animateBackgroundIn(plane)
            }
          }
        }

        if (this.isReducedMotion) {
          progress.set(1, 1, 1, 1)
          ditheringAnimation.render(0, 0)
        } else {
          const time = performance.now() - plane.userData.startTime
          progress.x += (1.0 - progress.x) * Math.min(1, frameDelta * 2.5)
          progress.y += (1.0 - progress.y) * Math.min(1, frameDelta * 1)

          if (time > 500) {
            progress.z += (1.0 - progress.z) * Math.min(1, frameDelta * 1)
          }

          ditheringAnimation.render(frameDeltaScroll, frameDelta)
        }
      }
    }
  }

  private getFragmentShaderByType(type: DitherParams['type']) {
    switch (type) {
      case 'wave':
        return shaderWaveFrag
      case 'ripple':
        return shaderRippleFrag
      case 'noise':
        return shaderNoiseFrag
      case 'random': {
        const arr = [shaderWaveFrag, shaderRippleFrag, shaderNoiseFrag]
        const randomIndex = Math.floor(Math.random() * arr.length)
        return arr[randomIndex]
      }
      default:
        return shaderWaveFrag
    }
  }

  private createPlane(image: HTMLElement, ditherId: string, params: DitherParams) {
    const variant = VARIANTS[params.color] || VARIANTS.default
    const fragmentShader = this.getFragmentShaderByType(params.type)

    const rect = image.getBoundingClientRect()
    const ditheringAnimation = new DitheringAnimation(
      rect.width,
      rect.height,
      fragmentShader!,
      this.bayerTexture.fbo.texture,
    )
    // const resolution = new Vector2(rect.width, rect.height);

    const progress = new Vector4(0, 0, 0, 0)

    if (!params.fadeIn) {
      progress.set(1, 1, 1, 1)
      ditheringAnimation.uniforms.uProgress.value.set(1, 1, 1, 1)
    }

    // Clone variant uniforms to avoid shared state between planes using the same variant
    const variantUniforms = Object.fromEntries(
      Object.entries(variant.uniforms).map(([key, uniform]) => {
        const value = uniform.value
        return [key, {value: value instanceof Color ? value.clone() : value}]
      }),
    ) as DitheringVariantUniforms

    const uniforms = {
      uTexture: {value: ditheringAnimation.fbo.texture},
      uProgress: {value: progress},
      uDotOpacity: {value: params.dotOpacity ?? DITHERING_DEFAULT_DOT_OPACITY},
      ...this.commonUniforms,
      ...variantUniforms,
    } satisfies DitheringUniforms

    if (variant.backgroundType === 'noiseGradient') {
      ;(uniforms as DitheringUniforms).uAspect = {value: rect.width / rect.height}
    }

    const plane = new Mesh(
      new PlaneGeometry(1, 1),
      new ShaderMaterial({
        vertexShader,
        fragmentShader: BACKGROUND_FRAGMENT_SHADERS[variant.backgroundType],
        uniforms,
        transparent: true,
      }),
    )

    plane.userData.uniforms = uniforms
    plane.userData.domElement = image
    plane.userData.ditherId = ditherId
    plane.userData.progress = progress
    plane.userData.ditheringAnimation = ditheringAnimation
    plane.userData.backgroundType = variant.backgroundType
    plane.userData.fadeIn = params.fadeIn
    plane.userData.hasEnteredViewport = false
    plane.userData.hasAnimatedIn = false
    plane.userData.abortController = new AbortController()

    if (variant.backgroundType === 'noiseGradient') {
      plane.userData.backgroundVisibilityThreshold = variant.backgroundVisibilityThreshold

      if (IS_DEBUG) {
        this.debugNoiseGradient(plane)
      } else if (!this.isReducedMotion) {
        this.interactNoiseGradient(plane)
      }
    }

    this.group.add(plane)
    this.planes.push(plane)
  }

  private removePlane(plane: Mesh) {
    ;(plane.userData.abortController as AbortController | undefined)?.abort()
    if (plane.userData.backgroundType === 'noiseGradient') {
      gsap.killTweensOf(plane.userData.uniforms.uXOffset)
      gsap.killTweensOf(plane.userData.uniforms.uYOffset)
    }

    this.group.remove(plane)
  }

  private animateBackgroundIn(plane: Mesh) {
    if (plane.userData.backgroundType === 'noiseGradient' && plane.userData.fadeIn) {
      const variantUniforms = VARIANTS.pearl.uniforms as DitheringVariantUniformsNoiseGradient
      const dummy = {
        uXOffset: variantUniforms.uXOffset.value,
        uYOffset: variantUniforms.uYOffset.value,
      }

      return gsap.from(dummy, {
        uXOffset: variantUniforms.uXOffset.value * 0.5,
        uYOffset: variantUniforms.uYOffset.value * 0.5,
        duration: 3,
        ease: 'power4.out',
        onUpdate: () => {
          plane.userData.uniforms.uXOffset.value = dummy.uXOffset
          plane.userData.uniforms.uYOffset.value = dummy.uYOffset
        },
        onComplete: () => {
          plane.userData.hasAnimatedIn = true
        },
      })
    }
  }

  private debugNoiseGradient(plane: Mesh) {
    const image: HTMLElement = plane.userData.domElement
    const uniforms = plane.userData.uniforms
    const {signal} = plane.userData.abortController as AbortController
    let isDragging = false

    image.addEventListener(
      'mousedown',
      () => {
        isDragging = true
      },
      {signal},
    )

    window.addEventListener(
      'mouseup',
      () => {
        isDragging = false
      },
      {signal},
    )

    image.addEventListener(
      'mousemove',
      event => {
        if (!isDragging) return

        // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-input-handler
        const rect = image.getBoundingClientRect()
        const x = ((event.clientX - rect.left) / rect.width) * 10
        const y = ((event.clientY - rect.top) / rect.height) * 10
        ;(uniforms as DitheringVariantUniformsNoiseGradient).uXOffset.value = x
        ;(uniforms as DitheringVariantUniformsNoiseGradient).uYOffset.value = y

        console.log(`uXOffset=${x.toFixed(2)} uYOffset=${y.toFixed(2)}`)
      },
      {signal},
    )
  }

  private interactNoiseGradient(plane: Mesh) {
    const image: HTMLElement = plane.userData.domElement
    const uniforms = plane.userData.uniforms as DitheringVariantUniformsNoiseGradient
    const {signal} = plane.userData.abortController as AbortController

    const baseXOffset = uniforms.uXOffset.value
    const baseYOffset = uniforms.uYOffset.value
    const supportsHover = window.matchMedia('(hover: hover)').matches
    let offsetTween: gsap.core.Tween | null = null

    const dummy = {
      uXOffset: baseXOffset,
      uYOffset: baseYOffset,
    }

    const tweenOffset = (x: number, y: number, duration = 0.4) => {
      offsetTween?.kill()
      offsetTween = gsap.fromTo(
        dummy,
        {
          uXOffset: plane.userData.uniforms.uXOffset.value,
          uYOffset: plane.userData.uniforms.uYOffset.value,
        },
        {
          uXOffset: x,
          uYOffset: y,
          duration,
          ease: 'power4.out',
          onUpdate: () => {
            plane.userData.uniforms.uXOffset.value = dummy.uXOffset
            plane.userData.uniforms.uYOffset.value = dummy.uYOffset
          },
        },
      )
    }

    const maxShiftX: [number, number] = [-0.1, 0.1]
    const maxShiftY: [number, number] = [-0.1, 0.1]
    const updateOffset = (clientX: number, clientY: number) => {
      const rect = image.getBoundingClientRect()
      const aspect = (plane.userData.uniforms as DitheringUniforms).uAspect!.value

      const dx = ((clientX - rect.left) / rect.width) * 2 - 1
      const dy = ((clientY - rect.top) / rect.height) * 2 - 1
      const shiftX = (dx >= 0 ? dx * maxShiftX[1] : dx * -maxShiftX[0]) * aspect
      const shiftY = -1 * (dy >= 0 ? dy * maxShiftY[1] : dy * -maxShiftY[0])

      tweenOffset(baseXOffset + shiftX, baseYOffset + shiftY)
    }

    const resetOffset = () => {
      tweenOffset(baseXOffset, baseYOffset, 0.8)
    }

    if (supportsHover) {
      image.addEventListener(
        'mousemove',
        event => {
          updateOffset(event.clientX, event.clientY)
        },
        {signal},
      )
      image.addEventListener('mouseleave', resetOffset, {signal})
      return
    }

    let isDragging = false
    image.addEventListener(
      'touchstart',
      () => {
        isDragging = true
      },
      {passive: true, signal},
    )

    window.addEventListener(
      'touchend',
      () => {
        if (!isDragging) return
        isDragging = false
        resetOffset()
      },
      {passive: true, signal},
    )

    image.addEventListener(
      'touchmove',
      event => {
        const touch = event.touches[0]
        if (!isDragging || !touch || !plane.userData.hasAnimatedIn) return
        updateOffset(touch.clientX, touch.clientY)
      },
      {passive: true, signal},
    )
  }
}
